import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateArealplan, arealplanInputSchema } from '@origo/planregister';
import { useToast } from '@origo/origo-components';
import NewPlanPage from './NewPlanPage';
import { useCodelistOptions } from '../../hooks/useCodelistOptions';
import {
    emptyNewPlanForm,
    rememberNewPlanDefaults,
    toNewArealplanInput,
} from '../../mappers/toNewArealplan';
import type { NewPlanFormValues } from '../../types';

type FieldErrors = Partial<Record<keyof NewPlanFormValues, string>>;

interface NewPlanContainerProps {
    /** Kalt med ny napId etter vellykket opprettelse. */
    onCreated: (napId: string) => void;
    /** Avbryt opprettelse (Forkast). */
    onCancel: () => void;
}

const NewPlanContainer: React.FC<NewPlanContainerProps> = ({ onCreated, onCancel }) => {
    const { t } = useTranslation();
    const toast = useToast();
    const codelists = useCodelistOptions();
    const create = useCreateArealplan();
    const [draft, setDraft] = useState<NewPlanFormValues>(() => emptyNewPlanForm());
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

    const handleChange = useCallback(
        <K extends keyof NewPlanFormValues>(field: K, value: NewPlanFormValues[K]) => {
            setDraft(d => ({ ...d, [field]: value }));
            // Rydd feltets feilmelding så snart brukeren retter det.
            setFieldErrors(e => (e[field] ? { ...e, [field]: undefined } : e));
        },
        []
    );

    const handleSave = useCallback(async () => {
        const input = toNewArealplanInput(draft);

        // Del samme zod-schema som pakken validerer mot — gir feltnære feil før innsending.
        const parsed = arealplanInputSchema.safeParse(input);
        if (!parsed.success) {
            const next: FieldErrors = {};
            for (const issue of parsed.error.issues) {
                const key = issue.path[0] as keyof NewPlanFormValues | undefined;
                if (key && !next[key]) next[key] = issue.message;
            }
            setFieldErrors(next);
            return;
        }
        setFieldErrors({});

        // toast.execute viser suksess-melding, og ved feil pakkens feilmelding
        // (ValidationError ved duplikat planId, NetworkError, ...).
        const plan = await toast.execute(() => create.mutateAsync(input), {
            success: t('newPlan.created'),
        });
        if (plan) {
            rememberNewPlanDefaults(draft);
            onCreated(plan.nasjonalArealplanId);
        }
    }, [draft, toast, t, create, onCreated]);

    return (
        <NewPlanPage
            values={draft}
            fieldErrors={fieldErrors}
            options={codelists}
            optionsLoading={codelists.isLoading}
            onChange={handleChange}
            onCancel={onCancel}
            onSave={handleSave}
            isSaving={create.isPending}
        />
    );
};

export default NewPlanContainer;
